import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Loader2, Shield, CheckCircle2, X, AlertTriangle, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { uploadPhoto, updateProfile, getProfile, getNotifications } from '../services/api';

const ALLOWED = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE = 10 * 1024 * 1024;

const PhotoRequiredPage = () => {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [removalReason, setRemovalReason] = useState(null);
  const [status, setStatus] = useState(user?.photo_status || 'required');
  const fileRef = useRef(null);

  useEffect(() => {
    // Refetch current profile + last photo_removed notification so we can show the removal reason
    (async () => {
      try {
        const r = await getProfile();
        setStatus(r.data.photo_status || 'required');
        updateUser(r.data);
        // If already fixed (photo present), auto-return
        if (r.data.photo_status === 'uploaded' && (r.data.photos || []).length > 0) {
          const back = (() => { try { return localStorage.getItem('gv_after_photo_return'); } catch { return null; } })();
          try { localStorage.removeItem('gv_after_photo_return'); } catch { /* ignore */ }
          navigate(back && !back.startsWith('/photo-required') ? back : '/home', { replace: true });
          return;
        }
      } catch { /* ignore */ }
      try {
        const n = await getNotifications(false, 20);
        const removal = (n.data.items || []).find((i) => i.type === 'photo_removed_by_admin');
        if (removal) setRemovalReason(removal.meta?.reason_label || null);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    if (!ALLOWED.includes(file.type)) {
      setError('Please choose a JPG, PNG, or WEBP image.');
      return;
    }
    if (file.size > MAX_SIZE) {
      setError('Image must be smaller than 10MB.');
      return;
    }
    setUploading(true);
    try {
      const up = await uploadPhoto(file);
      const url = `${process.env.REACT_APP_BACKEND_URL}${up.data.url}`;
      const saved = await updateProfile({ photos: [url] });
      updateUser(saved.data);
      setStatus('uploaded');
      // Return the user to where they were headed
      const back = (() => { try { return localStorage.getItem('gv_after_photo_return'); } catch { return null; } })();
      try { localStorage.removeItem('gv_after_photo_return'); } catch { /* ignore */ }
      navigate(back && !back.startsWith('/photo-required') ? back : '/home', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Upload failed. Please try another image.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const wasRemovedByAdmin = status === 'removed_by_admin';

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#009E60]/10 to-white flex items-center justify-center p-4" data-testid="photo-required-page">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl p-6">
        <div className={`w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-4 ${wasRemovedByAdmin ? 'bg-red-100' : 'bg-[#009E60]/10'}`}>
          {wasRemovedByAdmin
            ? <AlertTriangle className="w-8 h-8 text-red-500" />
            : <Camera className="w-8 h-8 text-[#009E60]" />}
        </div>
        <h1 className="text-2xl font-bold text-gray-900 text-center" style={{ fontFamily: 'Outfit' }}>
          {wasRemovedByAdmin ? 'Profile photo removed' : 'Add your profile photo'}
        </h1>
        <p className="text-sm text-gray-600 text-center mt-2">
          {wasRemovedByAdmin
            ? 'Your GuyanaVibes profile photo has been removed because it does not meet our profile-photo guidelines. Please upload a new profile photo to continue.'
            : 'A profile photo is required on GuyanaVibes so members can see who they’re connecting with. Upload one to continue.'}
        </p>

        {wasRemovedByAdmin && removalReason && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-sm text-red-800 flex items-start gap-2" data-testid="removal-reason">
            <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div><span className="font-semibold">Reason:</span> {removalReason}</div>
          </div>
        )}

        <div className="mt-5 bg-gray-50 rounded-2xl p-3 text-xs text-gray-600 space-y-1">
          <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#009E60]" /> Clear photo of yourself (JPG, PNG, or WEBP)</div>
          <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#009E60]" /> No nudity, hate, or advertisements</div>
          <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#009E60]" /> Max 10MB — the primary photo unlocks discovery</div>
        </div>

        {error && (
          <div className="mt-3 bg-red-50 text-red-600 rounded-xl px-3 py-2 text-sm flex items-center gap-2" data-testid="upload-error">
            <X className="w-4 h-4" /> {error}
          </div>
        )}

        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} className="hidden" data-testid="photo-file-input" />
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="w-full h-12 mt-5 bg-[#009E60] hover:bg-[#007A4B] rounded-full text-white font-semibold"
          data-testid="upload-photo-btn"
        >
          {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
          {uploading ? 'Uploading…' : 'Upload profile photo'}
        </Button>

        <button onClick={() => { logout(); navigate('/login'); }} className="mt-4 w-full text-xs text-gray-500 flex items-center justify-center gap-1" data-testid="logout-btn">
          <LogOut className="w-3 h-3" /> Sign out
        </button>
      </div>
    </div>
  );
};

export default PhotoRequiredPage;
